import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import _ from "lodash";
import { api } from "../shared/utilities/api";
import apiConfigs from "../shared/utilities/apiConfigs";

import PatientLoginForm from "./PatientLoginForm";
import PatientDashboard from "./PatientDashboard";

const DEFAULT_LOGO =
	"https://porton-shared.s3.ca-central-1.amazonaws.com/images/Porton_New_Logo.png";

// ── Inline SVG icons (replaces MUI icon imports) ──────────────
const LocationIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="rgba(255,255,255,0.9)"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
		<circle cx="12" cy="10" r="3" />
	</svg>
);

const PhoneIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="rgba(255,255,255,0.9)"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.7a16 16 0 0 0 6 6 .9.9 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21.5 16z" />
	</svg>
);

const PatientPage = () => {
	const { orgId } = useParams();

	const [logo, setLogo] = useState(DEFAULT_LOGO);
	const [orgInfo, setOrgInfo] = useState({
		address: "",
		contactNum: "",
		email: "",
		whiteLabelURL: "",
		org_name: "",
	});
	const [loggedInPatient, setLoggedInPatient] = useState(null);
	const [bookHover, setBookHover] = useState(false);

	const getOrgDetails = async (id) => {
		try {
			const result = await api(
				apiConfigs.baseOption("get", `orgs/${id}/getReport`),
			);

			if (result?.status === 200) {
				const orgLogo = _.get(result, "data.logo", DEFAULT_LOGO);
				if (orgLogo) setLogo(orgLogo);

				setOrgInfo({
					address: _.get(result, "data.address", ""),
					contactNum: _.get(result, "data.contactNum", ""),
					email: _.get(result, "data.email", ""),
					whiteLabelURL: _.get(result, "data.whiteLabelURL", ""),
					org_name: _.get(result, "data.org_name", ""),
				});
			}
		} catch (error) {
			console.error("Error retrieving org details:", error);
		}
	};

	useEffect(() => {
		if (!orgId) return;

		getOrgDetails(orgId);

		const localPatient = localStorage.getItem(`${orgId}:patient`);
		if (localPatient) {
			try {
				setLoggedInPatient(JSON.parse(localPatient));
			} catch {
				localStorage.removeItem(`${orgId}:patient`);
			}
		}
	}, [orgId]);

	const handleLoginSuccess = (patient) => {
		setLoggedInPatient(patient);
		localStorage.setItem(`${orgId}:patient`, JSON.stringify(patient));
	};

	const patientLogout = () => {
		localStorage.removeItem(`${orgId}:patient`);
		setLoggedInPatient(null);
		window.location.reload();
	};

	const hasOrgInfo = orgInfo.address || orgInfo.contactNum;

	return (
		<div
			style={{
				minHeight: "100vh",
				background: "#F3F4F6",
				fontFamily: "Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif",
				overflowX: "hidden",
			}}
		>
			{/* ── Scoped CSS for child MUI components + responsive breakpoints ── */}
			<style>{`
				.patient-scope * { box-sizing: border-box; }
				.patient-scope { font-family: Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif; }

				.patient-card .MuiInputBase-root { border-radius: 12px; background: #fff; }
				.patient-card .MuiOutlinedInput-input { padding: 14px 14px; font-size: 16px; }
				.patient-card .MuiOutlinedInput-notchedOutline { border-color: rgba(15,23,42,0.18); }
				.patient-card .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
					border-color: rgba(234,131,49,0.95);
				}
				.patient-card .MuiOutlinedInput-root.Mui-focused {
					box-shadow: 0 0 0 4px rgba(234,131,49,0.18);
				}
				.patient-card .MuiFormControl-root { margin: 0 0 16px 0; }
				.patient-card .MuiFormLabel-root {
					display: block;
					font-size: 13px;
					font-weight: 500;
					color: #111827;
					margin: 0 0 6px 0;
				}
				.patient-card .MuiButton-root {
					text-transform: none;
					font-weight: 500;
					border-radius: 999px;
					height: 52px;
					font-size: 16px;
					transition: background 160ms ease;
				}
				.patient-card .MuiButton-contained,
				.patient-card .MuiButton-containedPrimary {
					background: #EA8331;
					color: #fff;
				}
				.patient-card .MuiButton-contained:hover,
				.patient-card .MuiButton-containedPrimary:hover {
					background: #C96820;
				}
				.patient-card .MuiButton-root.Mui-disabled {
					background: #E5C9B0 !important;
					color: rgba(255,255,255,0.7) !important;
				}

				/* ── Responsive layout ── */
				.pp-hero-top {
					display: flex;
					align-items: center;
					justify-content: space-between;
					gap: 16px;
					flex-wrap: wrap;
				}
				.pp-brand-row {
					display: flex;
					align-items: center;
					gap: 14px;
					min-width: 0;
					flex-wrap: wrap;
				}
				.pp-clinic-identity {
					display: flex;
					align-items: center;
					gap: 14px;
					min-width: 0;
				}
				/* Translucent backing behind the clinic logo — sized to the logo's
				   own aspect ratio (height-driven, width auto) so wide/horizontal
				   logos get a wider container and circular/square logos land in a
				   balanced square container. Mirrors Book App's logo-card. */
				.pp-logo-card {
					display: flex;
					align-items: center;
					justify-content: center;
					height: 130px;
					width: auto;
					min-width: 130px;
					max-width: 320px;
					padding: 10px 20px;
					border-radius: 8px;
					background: radial-gradient(
						ellipse at center,
						rgba(255,255,255,0.32) 0%,
						rgba(255,255,255,0.1) 70%,
						rgba(255,255,255,0) 100%
					);
					flex-shrink: 0;
				}
				.pp-logo {
					height: 100%;
					width: auto;
					max-width: 100%;
					object-fit: contain;
					display: block;
				}
				.pp-clinic-name {
					font-size: 22px;
					font-weight: 700;
					color: #ffffff;
					line-height: 1.2;
					letter-spacing: -0.01em;
					min-width: 0;
					word-break: break-word;
				}
				.pp-divider {
					width: 1px;
					height: 32px;
					background: rgba(255,255,255,0.3);
					flex-shrink: 0;
				}
				.pp-info-row {
					display: flex;
					gap: 20px;
					flex-wrap: wrap;
				}

				@media (max-width: 768px) {
					.pp-logo-card {
						height: 104px;
						min-width: 104px;
						max-width: 260px;
						padding: 8px 16px;
					}
					.pp-clinic-name {
						font-size: 19px;
					}
				}

				@media (max-width: 600px) {
					.pp-hero-top {
						flex-direction: column;
						align-items: flex-start;
					}
					.pp-clinic-identity {
						flex-direction: column;
						align-items: flex-start;
						gap: 10px;
					}
					.pp-logo-card {
						height: 80px;
						min-width: 80px;
						max-width: 200px;
						padding: 6px 12px;
					}
					.pp-clinic-name {
						font-size: 17px;
					}
					.pp-divider {
						height: 26px;
					}
					.pp-book-btn {
						width: 100%;
						text-align: center;
					}
				}

				@media (max-width: 380px) {
					.pp-brand-row {
						gap: 10px;
					}
					.pp-logo-card {
						height: 72px;
						min-width: 72px;
						max-width: 180px;
					}
					.pp-clinic-name {
						font-size: 16px;
					}
				}
			`}</style>

			<div
				className="patient-scope"
				style={{
					width: "100%",
					maxWidth: 760,
					margin: "0 auto",
				}}
			>
				{/* ── Orange hero banner ── */}
				<div
					style={{
						background: "linear-gradient(135deg, #EA8331 0%, #C96820 100%)",
						padding: "28px 20px 56px",
					}}
				>
					{/* Logo + Book button row */}
					<div
						className="pp-hero-top"
						style={{
							marginBottom: hasOrgInfo ? 20 : 0,
						}}
					>
						<div className="pp-brand-row">
							<div className="pp-clinic-identity">
								<div className="pp-logo-card">
									<img
										className="pp-logo"
										src={logo}
										alt={orgInfo.org_name || "Clinic logo"}
									/>
								</div>
								{orgInfo.org_name && (
									<span className="pp-clinic-name">
										{orgInfo.org_name}
									</span>
								)}
							</div>

							<div className="pp-divider" />

							<a
								href="https://www.portonhealth.com/"
								target="_blank"
								rel="noopener noreferrer"
								style={{
									display: "flex",
									flexDirection: "column",
									gap: 2,
									textDecoration: "none",
								}}
							>
								<span
									style={{
										fontSize: 10,
										color: "rgba(255,255,255,0.6)",
										textTransform: "uppercase",
										letterSpacing: "0.08em",
										fontWeight: 500,
										fontFamily: "Roboto, sans-serif",
									}}
								>
									Powered by
								</span>
								<span
									style={{
										fontSize: 15,
										fontWeight: 700,
										color: "rgba(255,255,255,0.88)",
										fontFamily: "Roboto, sans-serif",
										letterSpacing: "-0.01em",
									}}
								>
									Porton
								</span>
							</a>
						</div>

						{orgInfo.whiteLabelURL && (
							<a
								href={orgInfo.whiteLabelURL}
								target="_blank"
								rel="noopener noreferrer"
								onMouseEnter={() => setBookHover(true)}
								onMouseLeave={() => setBookHover(false)}
								className="pp-book-btn"
								style={{
									padding: "9px 18px",
									borderRadius: 20,
									border: "1.5px solid rgba(255,255,255,0.55)",
									background: bookHover
										? "rgba(255,255,255,0.18)"
										: "rgba(255,255,255,0.1)",
									color: "#FFFFFF",
									fontSize: 13,
									fontWeight: 500,
									textDecoration: "none",
									whiteSpace: "nowrap",
									transition: "background 0.15s",
									flexShrink: 0,
									boxSizing: "border-box",
								}}
							>
								Book Appointment
							</a>
						)}
					</div>

					{/* Clinic info row */}
					{hasOrgInfo && (
						<div className="pp-info-row">
							{orgInfo.address && (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 7,
										fontSize: 13,
										color: "rgba(255,255,255,0.88)",
										minWidth: 0,
									}}
								>
									<LocationIcon />
									{orgInfo.address}
								</div>
							)}

							{orgInfo.contactNum && (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 7,
										fontSize: 13,
										color: "rgba(255,255,255,0.88)",
									}}
								>
									<PhoneIcon />
									{orgInfo.contactNum}
								</div>
							)}
						</div>
					)}
				</div>

				{/* ── Main card overlapping hero ── */}
				<div style={{ padding: "0 16px 48px", marginTop: -32 }}>
					<div
						className="patient-card"
						style={{
							background: "#FFFFFF",
							borderRadius: 18,
							border: "0.5px solid rgba(15,23,42,0.07)",
							boxShadow: "0 12px 40px rgba(15,23,42,0.12)",
							padding: "28px 24px",
							width: "100%",
						}}
					>
						{loggedInPatient?.demographicNo ? (
							<PatientDashboard
								orgId={orgId}
								patient={loggedInPatient}
								patientLogout={patientLogout}
								orgContactNum={orgInfo.contactNum}
							/>
						) : (
							<PatientLoginForm
								orgId={orgId}
								handleLoginSuccess={handleLoginSuccess}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default PatientPage;